import { useTranslation } from 'react-i18next';

import { useLanguage } from '../../app/i18n';
import {
  FRESHNESS_TIME_SLOT,
  type FreshnessFailure,
  type FreshnessIndicatorProps,
} from '../../components/FreshnessIndicator';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';
import { useIsFresh } from '../../lib/api/use-is-fresh';

import { formatClockTime } from './format-time';

/** What a polled screen knows about the data it shows (`useApiQuery` with `poll`, M7). */
export interface FreshnessInput {
  /** When the server read the data: `generatedAt` of `GET /me`, `refreshedAt` of `GET /positions` (C1). */
  readonly asOf: string;
  /** When that data arrived, on the device clock (`receivedAt` of `useApiQuery`, M7 R9). */
  readonly receivedAt: number;
  /** A background refresh is in flight (M7 R5). */
  readonly refreshing: boolean;
  /** Failure of the last refresh while the last data stays on screen (M7 R7). */
  readonly refreshError: ApiError | undefined;
}

/**
 * The props of `FreshnessIndicator` for one polled screen (M7 §4), so Home (E6) and Positions
 * (E8) translate it the same way: the `freshness.*` texts, the server time as HH:MM:SS in the UI
 * language, freshness judged on the arrival time (`useIsFresh`, 15 s, Q60), and the failure kind.
 *
 * Offline only when nothing answered (`ApiError.network`, set by the `fetch` catch of the API
 * client): a network failure has no reason code (C2 §2). Any other failure, a status-0 error with
 * a reason (session renewal refused) included, shows its reason text (I12), never a raw code.
 */
export function useFreshness({
  asOf,
  receivedAt,
  refreshing,
  refreshError,
}: FreshnessInput): FreshnessIndicatorProps {
  const { t } = useTranslation();
  const language = useLanguage();
  const fresh = useIsFresh(receivedAt);
  let failure: FreshnessFailure | undefined;
  if (refreshError !== undefined) {
    if (refreshError.network) {
      failure = { kind: 'offline' };
    } else {
      const reason = reasonMessage(refreshError.reasonCode, refreshError.params);
      failure = { kind: 'refused', reason: t(reason.key, reason.values) };
    }
  }
  const slot = { time: FRESHNESS_TIME_SLOT };
  return {
    time: formatClockTime(asOf, language, { seconds: true }),
    fresh,
    refreshing,
    failure,
    texts: {
      upToDate: t('freshness.upToDate'),
      updatedAt: t('freshness.updatedAt', slot),
      offline: t('freshness.offline', slot),
      failed: t('freshness.failed', slot),
      refreshing: t('freshness.refreshing'),
    },
  };
}
